import { useState } from "react";
import {
	List,
	ListItem,
	Box,
	Collapse,
	Typography,
	Button,
	Tooltip,
} from "@mui/material";
import ColorDisplayAndPicker from "../../../components/common/ColorDisplayAndPicker";
import {
	useEditCategoryGroupMutation,
	useSaveBudgetMutation,
} from "../../../services/queryHooks";
import { useTranslation } from "react-i18next";
import { ExpandLess, ExpandMore, Loop } from "@mui/icons-material";
import SubcategoryBudgetList from "./SubcategoryBudgetList";
import { useTheme } from "@emotion/react";
import AddOrEditBudgetModal from "./AddOrEditBudgetModal";

export default function CategoryBudgetList({ group, type, period }) {
	const {
		color,
		groupName,
		groupTranslationKey,
		subcategories,
		totalBudget,
		totalGroupTransactionsSum,
		groupId,
		maximalSubBudget,
	} = group;

	const [isExpanded, setIsExpanded] = useState(true);
	const [isAddOrEditModalOpen, setIsAddOrEditModalOpen] = useState(false);
	const editCategoryGroupMutation = useEditCategoryGroupMutation();
	const { mutate: saveBudget, isPending } = useSaveBudgetMutation();

	const { t } = useTranslation();
	const theme = useTheme();

	const userCurrency = localStorage.getItem("userCurrency");

	const handleColorChange = (color) => {
		editCategoryGroupMutation.mutate({
			categoryGroupId: group.groupId,
			color: color,
		});
	};
	const handleSubmit = (data) => {
		saveBudget(data);
	};

	const balanceStyles = {
		color: (
			type === "expense"
				? totalGroupTransactionsSum <= totalBudget
				: totalGroupTransactionsSum >= totalBudget
		)
			? theme.palette.values.positive
			: theme.palette.values.negative,
	};

	return (
		<>
			<List sx={{ pl: 1, py: 0 }}>
				<ListItem
					sx={{
						pb: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<Box sx={{ display: "flex", alignItems: "baseline" }}>
							<ColorDisplayAndPicker
								color={color}
								onColorChange={handleColorChange}
								isEditable={true}
							/>
							<Typography
								variant="body1"
								sx={{
									fontWeight: "bold",
								}}
							>
								{groupName ? groupName : t(groupTranslationKey)}
							</Typography>
						</Box>
						{subcategories.length !== 0 &&
							(isExpanded ? (
								<ExpandLess
									sx={{ cursor: "pointer" }}
									onClick={() => setIsExpanded(!isExpanded)}
								/>
							) : (
								<ExpandMore
									sx={{ cursor: "pointer" }}
									onClick={() => setIsExpanded(!isExpanded)}
								/>
							))}
					</Box>
					<Box sx={{ display: "flex" }}>
						<Typography
							variant="body1"
							sx={{
								fontWeight: "bold",
								...balanceStyles,
							}}
						>
							{Math.abs(totalGroupTransactionsSum)
								.toFixed(2)
								.toLocaleString()}{" "}
							{userCurrency}
						</Typography>
						<Typography
							variant="body1"
							sx={{
								ml: 0.5,
								fontWeight: "bold",
							}}
						>
							{"/"}
						</Typography>
						<Button
							color="primary"
							variant="text"
							onClick={() => setIsAddOrEditModalOpen(true)}
							sx={{
								py: 0,
								px: 0.5,
							}}
						>
							{" "}
							{group.isTotalBudgetRecurring ? (
								<Tooltip
									title={t("recurringBudget")}
									placement="top"
									arrow
								>
									<span>
										<Loop
											sx={{
												width: "19px",
												verticalAlign: "sub",
											}}
										/>
									</span>
								</Tooltip>
							) : null}{" "}
							<Typography
								variant="body1"
								sx={{
									fontWeight: "bold",
								}}
							>
								{Math.abs(totalBudget)
									.toFixed(2)
									.toLocaleString()}{" "}
								{userCurrency}
							</Typography>
						</Button>
					</Box>
				</ListItem>
				<Collapse in={isExpanded}>
					<SubcategoryBudgetList
						subcategories={subcategories}
						type={type}
						categoryGroupId={groupId}
						period={period}
						categoryGroupName={groupName}
						maximalSubBudget={maximalSubBudget}
					/>
				</Collapse>
			</List>
			<AddOrEditBudgetModal
				isOpen={isAddOrEditModalOpen}
				onClose={() => setIsAddOrEditModalOpen(false)}
				initialData={{
					budgetId:
						group.totalBudgetSource === "currentPeriod"
							? group.totalBudgetId
							: "",
					categoryId: "",
					categoryName: "",
					categoryGroupId: group.groupId,
					categoryGroupName: group.groupName,
					date: period,
					amount: group.totalBudget,
					minAmount: group.minimalTotalBudget,
					maxAmount: group.maximalSubBudget,
					type,
					totalBudget: true,
					recurring:
						group.totalBudgetSource === "currentPeriod" &&
						group.isTotalBudgetRecurring === false
							? false
							: true,
				}}
				onSubmit={handleSubmit}
				isPending={isPending}
			/>
		</>
	);
}
